import {computeHasAnyFrozenColumns} from 'notebook/tables/store/logic/TableAffixedLogic';
import {createTableModelSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';

/**
 * Model describing which columns are affixed (frozen to the left edge) and whether the table is
 * currently scrolled far enough right for the affixed columns to cast their shadow.
 *
 * The per-cell consequences of these values (which cell is sticky, how far from the left edge it
 * sits, which cell renders the divider) are derived in `getTableCellAffixedProps`.
 */
export interface TableAffixedModel {
  /**
   * The number of leading columns that are frozen, counting both user-frozen columns and the
   * adapter's default. Zero when nothing is frozen.
   */
  affixedColumnCount: number;

  /**
   * Whether the user has frozen any columns, which is what makes the last frozen column render
   * its divider. Adapter-defaulted affixed columns do not count.
   */
  hasAnyFrozenColumns: boolean;

  /**
   * Whether the table is scrolled horizontally, so the last affixed column should cast a shadow
   * over the content scrolling underneath it.
   */
  showAffixedShadow: boolean;
}

/**
 * Creates a memoized selector that computes the TableAffixedModel from the store.
 */
export function createTableAffixedSelector(): (store: TableEditableStore) => TableAffixedModel {
  return createTableModelSelector(
    [
      (store) => TableUtil.getAffixedColumnCount(store.volatileProps.table),
      (store) => store.volatileProps.table.frozenColumnCount,
      (store) => store.state.isAffixed,
    ],
    (affixedColumnCount, frozenColumnCount, isAffixed) => ({
      affixedColumnCount,
      hasAnyFrozenColumns: computeHasAnyFrozenColumns(frozenColumnCount),
      showAffixedShadow: isAffixed,
    })
  );
}
